/**
 * Screen-scoped design tokens for **Settings → Integrations**.
 *
 * This screen was built straight from the MUI theme and had drifted onto its
 * own chrome — an `h5` page title, a 16px-radius `neutral[200]` card. These
 * tokens restate the values the re-skinned settings screens already use (see
 * `EnterpriseSSO.tokens.ts`, `Settings.tokens.ts`) so Integrations reads as the
 * same product. Stays screen-local until the shared values are promoted into
 * the MUI theme.
 */

import { button, contentCard, notification, signer } from "theme/colors";

export const apiKeysTokens = {
  /** White rounded card chrome — identical chrome to every other Settings card. */
  card: {
    border: contentCard.borderMuted,
    borderWidth: "1px",
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
  },
  /** Strapline under the page title, in the same voice as Enterprise SSO's. */
  pageSubtitle: {
    color: signer.text.meta,
    fontSize: "12.5px",
    fontWeight: 500,
    lineHeight: "normal",
    marginTop: "4px",
  },
  /** Filled brand action ("Create API key"), straight off the global token. */
  primaryButton: button,
  /** Glyph + copy block heading the card. */
  sectionHeader: {
    titleFontSize: "14px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    descFontSize: "12px",
    descColor: signer.text.meta,
    glyphSize: 28,
    glyphRadius: "8px",
  },
  /**
   * The key list. Rebuilt from scratch off `MyPlan.tokens.ts` /
   * `MyPlan.styles.ts` — the "Invoices" list is the reference this list must
   * read as the same product as, column-header strip, row grid, pill and all,
   * whether the list is populated, empty, or the feature is off-plan. The
   * strip itself is unconditional in that reference (only the rows swap to an
   * empty message), which is why the header lives outside `renderContent`'s
   * loading/empty branches below.
   */
  list: {
    color: {
      ink: signer.ink,
      body: signer.text.meta,
      muted: signer.text.faint,
      rowDivider: signer.border.divider,
      headerBg: signer.surface.subtle,
      headerBorder: signer.border.light,
      accent: signer.orange.main,
      accentHover: signer.orange.dark,
    },
    font: {
      columnHeader: "10.5px",
      rowText: "13px",
      rowMeta: "12px",
      chip: "10.5px",
    },
    weight: {
      strong: 700,
      medium: 600,
      regular: 400,
    },
    letterSpacing: {
      columnHeader: "0.09em",
    },
    border: {
      hairline: "1px",
    },
    space: {
      gutter: "28px",
      columnHeaderY: "10px",
      rowY: "13px",
    },
    /** `name / key id / expires / status / actions`, verbatim proportions
     *  from `MyPlan.tokens.ts`'s `grid.invoiceRow` narrowed to five columns. */
    grid: "1.6fr 1.1fr 1fr 100px 90px",
    radius: { pill: "999px" },
  },
  /**
   * Status pill tone. Same stadium shape as the Invoices STATUS pill (Paid =
   * green); only the fill/text colour changes per state, since these mark
   * distinct states rather than one fixed label.
   */
  chip: {
    active: { bg: notification.success.bg, text: "#1E8A55" },
    expired: { bg: notification.error.bg, text: "#D93025" },
    revoked: { bg: signer.purple.chipBg, text: signer.purple.main },
  },
} as const;

export default apiKeysTokens;
